import moment from 'moment'
import { $enum } from 'ts-enum-util'
import * as yup from 'yup'
import { STOP_TYPE } from 'apps/fleet-planner/constants/loads'
import {
  ShipmentStopAddressSchema,
  IShipmentStopAddress,
} from 'apps/fleet-planner/schema/ShipmentStopAddressSchema'
import {
  IShipmentStopFacilityContact,
  ShipmentStopFacilityContactSchema,
} from 'apps/fleet-planner/schema/ShipmentStopFacilityContactSchema'
import {
  ShipmentStopFacilitySchema,
  IFacilityBase,
} from 'apps/fleet-planner/schema/ShipmentStopFacilitySchema'

const requiredMessage = 'This field is required.'

/**
 * Used to enable better typing in `ShipmentStopSchema` and
 * in `useFieldArray` context
 */
export interface IShipmentStop {
  id?: string
  stopSequence: number
  stopType: STOP_TYPE
  commodity?: string | null
  weight?: string
  unitQuantity?: string
  unit?: string | null
  isDropTrailer?: boolean
  poNumber?: string
  bolNumber?: string | null
  notes?: string
  referenceNumber?: string
  appointmentNumber?: string | null
  instructions?: string | null
  city?: string
  stateCode?: string
  address: Partial<IShipmentStopAddress>
  startAt?: string
  endAt?: string | null
  facility: IFacilityBase
  facilityContact: Partial<IShipmentStopFacilityContact> | null
  displayTimezone?: string
}

/**
 * Used to create a new shipment
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Shipments/post_shipments
 */
export const ShipmentStopSchema = yup.object<IShipmentStop>({
  id: yup.string().notRequired(),
  stopSequence: yup.number().required(),
  stopType: yup
    .mixed<STOP_TYPE>()
    .oneOf([...$enum(STOP_TYPE).getValues()])
    .required(),
  commodity: yup.string().notRequired().nullable().default(null),
  weight: yup.string().notRequired(),
  unitQuantity: yup.string().notRequired(),
  unit: yup.string().notRequired().nullable().default(null),
  poNumber: yup.string().notRequired(),
  bolNumber: yup.string().notRequired().nullable().default(null),
  notes: yup.string().notRequired(),
  referenceNumber: yup.string().notRequired(),
  appointmentNumber: yup.string().notRequired().nullable().default(null),
  instructions: yup.string().notRequired().nullable().default(null),
  city: yup.string().notRequired(),
  stateCode: yup.string().notRequired(),
  address: ShipmentStopAddressSchema,
  facilityContact: ShipmentStopFacilityContactSchema.nullable(),
  facility: ShipmentStopFacilitySchema,
  isDropTrailer: yup.boolean().default(false),
  startAt: yup.string().required(requiredMessage),
  /**
   * This validation ensures that the `endAt` date
   * is after the `startAt` date
   */
  endAt: yup
    .string()
    .notRequired()
    .nullable()
    .test(
      'is-after-startAt',
      'The cutoff time can’t be before the start time.',
      function validateEndAt(endAt: string) {
        const { startAt } = this.parent

        // If a date is present, validate it.
        // Otherwise, bypass this unrequired field
        return !!endAt && moment(endAt).isValid()
          ? moment(endAt).isSameOrAfter(moment(startAt))
          : true
      }
    ),
})

export type ShipmentStopSchemaType = yup.InferType<typeof ShipmentStopSchema>
